import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuthContext } from "@/lib/AuthProvider";
import HeroSection from "@/components/HeroSection";
import ShopByCategory from "@/components/ShopByCategory";
import FeaturedProducts from "@/components/FeaturedProducts";
import Features from "@/components/Features";
import LandingFAQ from "@/components/LandingFAQ";
import Testimonials from "@/components/Testimonials";
import Newsletter from "@/components/Newsletter";
import AuthModalWrapper from "@/components/AuthModalWrapper";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import { getHomepageSEO, validateSEOData, generateKeywords } from "@/services/seoService";

const Index = () => {
  const location = useLocation();
  const { logout } = useAuthContext();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState<"login" | "register">("login");
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const newsletterRef = useRef<HTMLDivElement>(null);

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  useEffect(() => {
    const state = location.state as any;
    
    if (state?.signOut) {
      // Sign out the user
      logout().catch(console.error);
    } else if (state?.openForgotPassword) {
      // Open the forgot password modal directly
      setForgotPasswordOpen(true);
    } else if (state?.openAuthModal) {
      // Open the auth modal
      setAuthModalOpen(true);
      if (state?.defaultTab) {
        setDefaultTab(state.defaultTab);
      }
    }

    // Clear the state after handling it
    if (state) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state, logout]);

  // Handle hash-based navigation to newsletter section with navbar height awareness
  useEffect(() => {
    const handleHashNavigation = () => {
      if (location.hash === '#newsletter' && newsletterRef.current) {
        const scrollToNewsletter = () => {
          const newsletterElement = newsletterRef.current;
          if (newsletterElement) {
            // Calculate the scroll position accounting for navbar height
            const elementTop = newsletterElement.offsetTop;
            const scrollPosition = elementTop - totalNavHeight;
            
            // Scroll to position with smooth behavior
            window.scrollTo({
              top: Math.max(0, scrollPosition),
              behavior: 'smooth'
            });
          }
        };

        // Wait for the page to be fully loaded and rendered
        if (document.readyState === 'complete') {
          // Page is already loaded, wait a bit more for components to render
          setTimeout(scrollToNewsletter, 500);
        } else {
          // Wait for the page to fully load
          const handleLoad = () => {
            // Additional delay to ensure all components are rendered
            setTimeout(scrollToNewsletter, 500);
            window.removeEventListener('load', handleLoad);
          };
          window.addEventListener('load', handleLoad);
        }
      }
    };

    // Handle initial load with hash
    handleHashNavigation();

    // Handle hash changes (if user navigates with hash in URL)
    const handleHashChange = () => {
      handleHashNavigation();
    };

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location.hash, totalNavHeight]);

  const handleBackToLogin = () => {
    setForgotPasswordOpen(false);
    setAuthModalOpen(true);
    setDefaultTab("login");
  };

  // Generate SEO data for homepage
  const seoData = validateSEOData(getHomepageSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={generateKeywords(seoData.keywords)} />
        )}
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
        
        {/* Structured Data - JSON-LD Schema */}
        {seoData.schema && seoData.schema.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      
    <main className="scroll-optimized">
      <HeroSection />
      <ShopByCategory />
      <div className="hidden md:block">
        <FeaturedProducts />
      </div>
      <div className="hidden md:block">
        <Features />
      </div>
      <Testimonials />
      <LandingFAQ />
      <Newsletter ref={newsletterRef} />
      
      <AuthModalWrapper 
        isOpen={authModalOpen} 
        onOpenChange={setAuthModalOpen}
        defaultTab={defaultTab}
      />
      
      <ForgotPasswordModal
        isOpen={forgotPasswordOpen}
        onOpenChange={setForgotPasswordOpen}
        onBackToLogin={handleBackToLogin}
      />
    </main>
    </>
  );
};

export default Index;
